import type {
  EntityType,
  ConfidenceBand,
  MdmRun,
  MdmSource,
  MdmFieldMapping,
  MdmMatchConfig,
  MdmMatchConfigSuggestion,
  MdmCluster,
  MdmClusterDetail,
  MdmPairDecision,
  PagedResult,
  PairFilter,
  ConnectorProfile,
  EntityTypeDef,
  FieldMappingSuggestion,
  OracleOutputConfigRequest,
  OracleOutputConfigResponse,
  AuditDecisionFilters,
  AuditDecisionsResult,
  ConnectionPreset,
} from "../types";
import { clearAuth } from "../../../auth";

const BASE = "/api/v2/mdm";

// Methods the backend's CSRF middleware requires a double-submit token for
// (mirrors SAFE_METHODS in ztd_app/api/core/middleware/csrf.py — GET/HEAD/
// OPTIONS are exempt there, everything else needs X-CSRF-Token).
const CSRF_REQUIRED_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

// Standard JSON request — same pattern as apiv2.ts. MDM used to run in its
// own container with its own JWT secret (JWT_SECRET_KEY, different from
// ztd_app's ZTD_AUTH_JWT_SECRET_KEY), which is why this used to fetch a
// separate Bearer token from a (never-implemented) /api/v1/auth/mdm-token
// endpoint. Now that the MDM router lives inside ztd_app's own app, it
// shares the same session-cookie auth as every other v2 module.
async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(CSRF_REQUIRED_METHODS.has(method)
        ? {
            "X-CSRF-Token":
              document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
          }
        : {}),
      ...(init?.headers as Record<string, string>),
    },
    credentials: "include",
  });
  // A stale session mid-flow (e.g. during the multi-step Map wizard) hits
  // every other API client's expired-session handling (apiv2.ts,
  // shared/api/client.ts) but this one had none — the backend's raw 401
  // detail ("Authentication required.") was leaking straight into wizard
  // error banners with no recovery path. Match apiv2.ts's behavior.
  if (res.status === 401) {
    clearAuth();
    throw new Error("Session expired — please log in again.");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = body.detail;
    let message: string;
    if (typeof detail === "string") {
      message = detail;
    } else if (Array.isArray(detail)) {
      message = detail
        .map((d: { msg?: string }) => d.msg ?? JSON.stringify(d))
        .join("; ");
    } else if (detail !== undefined && detail !== null) {
      message = JSON.stringify(detail);
    } else {
      message = `MDM API error: ${res.status} ${res.statusText}`;
    }
    console.error("[MDM req] error", res.status, body);
    throw new Error(message || `MDM API error: ${res.status}`);
  }
  if (res.status === 204) return undefined as unknown as T;
  const json = await res.json();
  // Unwrap APIEnvelope {data, meta} — bare responses (connectors, excel) pass through
  return (
    json && typeof json === "object" && "data" in json ? json.data : json
  ) as T;
}

// S1 — Data Source Selection
export async function createRun(
  name: string,
  entityType: EntityType,
  projectId: string,
): Promise<MdmRun> {
  const raw = await req<Record<string, unknown>>(
    `/runs?project_id=${encodeURIComponent(projectId)}`,
    {
      method: "POST",
      body: JSON.stringify({ name, entity_type: entityType }),
    },
  );
  // Fall back to the request's own name/entityType/etc. if the backend
  // response is ever missing a field — same defensive normalization
  // getRunStatus()/listRuns() below already apply to their responses.
  // (`raw.id` used to always be undefined here, before mdm_runs grew real
  // name/entity_type columns: setRunId(run.id) baked a literal "undefined"
  // into the next request's URL, which the backend's UUID path validator
  // rejected with a 422.)
  return {
    id: (raw.id ?? raw.run_id) as string,
    name: (raw.name ?? name) as string,
    entity_type: (raw.entity_type ?? entityType) as EntityType,
    status: normalizeRunStatus(raw.status),
    trigger_mode: "manual",
    source_count: 0,
    created_at: (raw.created_at ?? "") as string,
    updated_at: (raw.created_at ?? "") as string,
  };
}

/**
 * Registers a source from a previously connected database connector. The
 * backend creates the source row immediately but bulk-inserts its records
 * in a background task (a 100k+ record source can take longer than the
 * ALB's idle timeout if awaited inline) — poll waitForSourceRegistration()
 * with the returned source_id before requesting field discovery.
 */
export async function registerSourceFromConnector(
  runId: string,
  connectorId: string,
  systemName: string,
): Promise<{ source_id: string; record_count: number }> {
  const raw = await req<Record<string, unknown>>(
    `/runs/${runId}/sources/from-connector`,
    {
      method: "POST",
      body: JSON.stringify({
        connector_id: connectorId,
        system_name: systemName,
      }),
    },
  );
  return {
    source_id: (raw.source_id ?? raw.id ?? "") as string,
    record_count: (raw.record_count ?? 0) as number,
  };
}

/**
 * Polls GET /runs/{runId}/sources/{sourceId}/status until the source's
 * background bulk-insert has landed all declared records, or fails/times
 * out. Throws so callers can surface it via their own error state.
 */
export async function waitForSourceRegistration(
  runId: string,
  sourceId: string,
  {
    intervalMs = 1000,
    maxAttempts = 300,
  }: { intervalMs?: number; maxAttempts?: number } = {},
): Promise<void> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const raw = await req<Record<string, unknown>>(
      `/runs/${runId}/sources/${sourceId}/status`,
    );
    if (raw.ready) return;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error(
    "Source registration is taking longer than expected. Please try again shortly.",
  );
}

/**
 * Kicks off normalization of a run's raw records so field discovery becomes
 * available. Nothing else advances a fresh run out of "created"/"normalizing"
 * until the full pipeline executes, so this must be called once after
 * registering all sources and before getFields().
 *
 * The backend runs the actual normalize loop in a background task and
 * returns immediately (status "normalizing", or "already_normalized" if the
 * run was already past this stage) — on large runs the loop itself can take
 * longer than the ALB's idle timeout. Callers must poll getRunStatus() until
 * its raw status leaves "normalizing" before calling getFields().
 */
export async function normalizeRun(runId: string): Promise<{ status: string }> {
  return req<{ status: string }>(`/runs/${runId}/normalize`, {
    method: "POST",
  });
}

/**
 * Polls getRunStatus() until normalization completes (raw status reaches
 * "blocking") or fails ("error"), or the attempt budget is exhausted.
 * Throws on failure/timeout so callers can surface it via their own error
 * state rather than spinning forever.
 */
export async function waitForNormalization(
  runId: string,
  {
    intervalMs = 1000,
    maxAttempts = 300,
  }: { intervalMs?: number; maxAttempts?: number } = {},
): Promise<void> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Read the raw status directly rather than through getRunStatus()'s
    // RunStatus mapping — that collapses created/normalizing/blocking into
    // a single "running" bucket, which is too coarse to detect completion.
    const raw = await req<Record<string, unknown>>(`/runs/${runId}`);
    if (raw.status === "blocking") return;
    if (raw.status === "error") {
      throw new Error((raw.error_message as string) || "Normalization failed.");
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error(
    "Normalization is taking longer than expected. Please try again shortly.",
  );
}

/**
 * Marks a run cancelled by operator request. The pipeline runs as an
 * in-process background task with no cooperative cancellation point, so
 * this cannot interrupt in-flight work — it stops the run from being
 * reported as active (so polling stops and a fresh run can be started
 * cleanly). No-op on an already-terminal run (backend returns 409).
 */
export async function cancelRun(runId: string): Promise<{ status: string }> {
  return req<{ status: string }>(`/runs/${runId}/cancel`, {
    method: "POST",
  });
}

export async function getFields(runId: string): Promise<MdmFieldMapping[]> {
  const res = await req<
    Record<string, unknown>[] | { fields: Record<string, unknown>[] }
  >(`/runs/${runId}/fields`);
  const raw: Record<string, unknown>[] = Array.isArray(res)
    ? res
    : ((res as { fields: Record<string, unknown>[] }).fields ?? []);
  return raw.map((f) => ({
    canonical_field: (f.canonical_field ?? f.field_name ?? "") as string,
    source_attribute: (f.source_attribute ?? f.field_name ?? "") as string,
    source_system: (f.source_system ??
      (Array.isArray(f.sources) ? f.sources[0] : "")) as string,
    auto_mapped: (f.auto_mapped ?? true) as boolean,
    confidence: (f.confidence ?? 1.0) as number,
    needs_review: (f.needs_review ?? false) as boolean,
    alternatives: (f.alternatives ?? []) as string[],
    null_rate: (f.null_rate ?? 0) as number,
  }));
}

// S2 — Rule Builder
export async function getMatchConfigs(
  projectId: string,
): Promise<MdmMatchConfig[]> {
  const raw = await req<MdmMatchConfig[] | { configs?: MdmMatchConfig[] }>(
    `/match-configs?project_id=${encodeURIComponent(projectId)}`,
  );
  return Array.isArray(raw) ? raw : (raw.configs ?? []);
}

// Map frontend MatchMethod names → backend COMPARATORS registry keys.
// The frontend exposes a richer vocabulary; the backend only registers
// the comparators actually implemented in comparators.py.
const METHOD_TO_COMPARATOR: Record<string, string> = {
  exact: "exact",
  jaro_winkler: "jaro_winkler",
  levenshtein: "typographical", // closest backend equivalent
  soundex: "phonetic",
  metaphone: "phonetic",
  token_set_ratio: "jaccard_tokens",
  date_normalized: "date",
  address_standardized: "typographical",
};

// Translate composite_rules (OR-of-AND) into the flat `fields` list the
// backend scorer expects (Fellegi-Sunter shape: field_name, comparator,
// m_prob, u_prob, weight_multiplier). Each unique field across all enabled
// rules produces one entry; duplicate fields keep the highest threshold.
function compositeRulesToFields(
  composite_rules: NonNullable<MdmMatchConfig["composite_rules"]>,
): Record<string, unknown>[] {
  const best = new Map<string, { comparator: string; threshold: number }>();
  for (const rule of composite_rules) {
    if (!rule.enabled) continue;
    for (const cond of rule.conditions) {
      const existing = best.get(cond.field);
      if (!existing || cond.threshold > existing.threshold) {
        best.set(cond.field, {
          comparator: METHOD_TO_COMPARATOR[cond.method] ?? "jaro_winkler",
          threshold: cond.threshold,
        });
      }
    }
  }
  return Array.from(best.entries()).map(([field, cfg]) => ({
    field_name: field,
    comparator: cfg.comparator,
    // Derive Fellegi-Sunter probs from threshold: high threshold → high m_prob
    m_prob: Math.min(0.99, 0.5 + cfg.threshold / 200),
    u_prob: Math.max(0.01, 0.5 - cfg.threshold / 200),
    weight_multiplier: 1.0,
  }));
}

export async function saveMatchConfig(
  config: Omit<MdmMatchConfig, "id" | "created_at" | "run_count">,
): Promise<MdmMatchConfig> {
  // Backend expects `fields` (Fellegi-Sunter) not `match_rules`/`composite_rules`.
  // Translate composite_rules → fields when present; fall back to match_rules shape.
  let fields: Record<string, unknown>[];
  if (config.composite_rules && config.composite_rules.length > 0) {
    fields = compositeRulesToFields(config.composite_rules);
  } else {
    fields = config.match_rules.map((r) => ({
      field_name: r.field,
      comparator: r.method,
      m_prob: r.m_probability,
      u_prob: r.u_probability,
      weight_multiplier: r.weight,
      // 0-100 UI percent -> 0-1 fraction. Previously dropped entirely, so the
      // per-field Threshold slider on the Field Weights tab had no effect on
      // the saved config at all (scorer.py never gated on it either).
      min_threshold: r.threshold != null ? r.threshold / 100 : 0,
    }));
  }

  const confidenceBands = (config as Record<string, unknown>)
    .confidence_bands as
    | { auto_merge_threshold?: number; hitl_lower_bound?: number }
    | undefined;

  const body = {
    run_id: (config as Record<string, unknown>).run_id,
    project_id: (config as Record<string, unknown>).project_id,
    name: config.name,
    version: config.version,
    fields,
    // Backend blocking_stage expects list[dict] e.g. [{strategy, fields}, ...]
    // Frontend MdmBlockingConfig is {strategy: string[], blocking_keys: string[]}
    blocking: config.blocking
      ? config.blocking.strategy.map((s) => ({
          strategy: s,
          fields: config.blocking.blocking_keys,
        }))
      : [],
    survivorship_rules: Object.fromEntries(
      config.survivorship_rules.map((r) => [
        r.field,
        { strategy: r.strategy, source_priority: r.source_priority },
      ]),
    ),
    force_hitl_rules: config.force_hitl_rules,
    auto_match_threshold: confidenceBands?.auto_merge_threshold ?? 0.8,
    no_match_threshold: confidenceBands?.hitl_lower_bound ?? 0.5,
    skip_llm_adjudication: config.skip_llm_adjudication ?? false,
  };
  const saved = await req<{
    config_id?: string;
    id?: string;
    name?: string;
    version?: string | number;
    created_at?: string;
    run_count?: number;
  }>("/match-configs", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return {
    ...config,
    id: saved.id ?? saved.config_id ?? "",
    name: saved.name ?? config.name,
    version: String(saved.version ?? config.version),
    created_at: saved.created_at ?? new Date().toISOString(),
    run_count: saved.run_count ?? 0,
  };
}

export async function suggestMatchConfig(
  runId: string,
): Promise<MdmMatchConfigSuggestion> {
  return req<MdmMatchConfigSuggestion>("/match-configs/suggest", {
    method: "POST",
    body: JSON.stringify({ run_id: runId }),
  });
}

export async function executeRun(
  runId: string,
  options?: { sampleSize?: number; stage?: string },
): Promise<void> {
  const params = options?.stage ? `?stage=${options.stage}` : "";
  return req<void>(`/runs/${runId}/execute${params}`, {
    method: "POST",
    body: JSON.stringify(
      options?.sampleSize ? { sample_size: options.sampleSize } : {},
    ),
  });
}

export async function autoDecideAllPairs(
  runId: string,
  decision: "match" | "no_match" = "match",
  bypassReason?: string,
): Promise<{ decided: number }> {
  return req<{ decided: number }>(
    `/runs/${runId}/pairs/auto-decide?decision=${decision}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ bypass_reason: bypassReason ?? "" }),
    },
  );
}

export async function batchDecidePairs(
  pairIds: string[],
  decision: "match" | "no_match" | "escalate",
  stewardNotes?: string,
): Promise<{
  decided: string[];
  errors: { pair_id: string; detail: string }[];
}> {
  return req("/pairs/batch-decide", {
    method: "POST",
    body: JSON.stringify({
      pair_ids: pairIds,
      decision,
      steward_notes: stewardNotes ?? "",
    }),
  });
}

// S3 — Golden Record Explorer
function normalizeCluster(raw: Record<string, unknown>): MdmCluster {
  const preview = (raw.golden_preview ??
    raw.golden_json ??
    raw.attributes ??
    {}) as Record<string, string>;
  return {
    id: (raw.id ?? raw.cluster_id ?? "") as string,
    run_id: (raw.run_id ?? "") as string,
    entity_type: (raw.entity_type ?? null) as
      | import("../types").EntityType
      | null,
    status: (raw.status ?? "active") as import("../types").ClusterStatus,
    // API returns member_count; type expects source_count
    source_count: (raw.source_count ?? raw.member_count ?? 1) as number,
    // API returns created_at; type expects last_updated
    last_updated: (raw.last_updated ??
      raw.updated_at ??
      raw.created_at ??
      "") as string,
    origin_band: (raw.origin_band ??
      raw.confidence_band ??
      "auto_merge") as import("../types").ConfidenceBand,
    // Map golden_preview → attributes so the card can render field values
    attributes: preview,
    // Extra fields the explorer view uses (not in MdmCluster type but safe to carry)
    ...(raw.golden_preview != null ? { golden_preview: preview } : {}),
    ...(raw.member_count != null ? { member_count: raw.member_count } : {}),
    ...(raw.created_at != null ? { created_at: raw.created_at } : {}),
  };
}

export async function searchGoldenRecords(
  runId: string,
  query: string,
  cursor?: string,
  mergedOnly?: boolean,
): Promise<PagedResult<MdmCluster>> {
  const params = new URLSearchParams({ q: query });
  if (cursor) params.set("cursor", cursor);
  // Server-side merged filter: the "Merged" tab must list every consolidated
  // golden, not just the merged ones on the loaded page (single-source
  // goldens can outnumber merged ones 20:1).
  if (mergedOnly) params.set("merged_only", "true");
  // Backend (GoldenRecordSearchService.search) returns {results, total,
  // next_cursor} — not {items, cursor, has_more}. has_more is derived since
  // the backend only signals "more" implicitly via a present next_cursor.
  const raw = await req<{
    results: Record<string, unknown>[];
    total?: number;
    next_cursor?: string | null;
  }>(`/runs/${runId}/golden-records?${params}`);
  return {
    items: (raw.results ?? []).map(normalizeCluster),
    total: raw.total ?? raw.results?.length ?? 0,
    cursor: raw.next_cursor ?? undefined,
    has_more: !!raw.next_cursor,
  };
}

export async function getGoldenRecord(
  clusterId: string,
): Promise<MdmClusterDetail> {
  const raw = await req<Record<string, unknown>>(
    `/golden-records/${clusterId}`,
  );
  return {
    ...normalizeCluster(raw),
    attribute_provenance: (raw.attribute_provenance ??
      []) as import("../types").MdmClusterDetail["attribute_provenance"],
    source_contributions: (raw.source_contributions ??
      []) as import("../types").MdmClusterDetail["source_contributions"],
    linked_records: (raw.linked_records ??
      []) as import("../types").MdmClusterDetail["linked_records"],
    history: (raw.history ??
      []) as import("../types").MdmClusterDetail["history"],
    lineage: (raw.lineage ??
      []) as import("../types").MdmClusterDetail["lineage"],
  } as import("../types").MdmClusterDetail;
}

export async function unmergeCluster(clusterId: string): Promise<void> {
  return req<void>(`/golden-records/${clusterId}/unmerge`, { method: "POST" });
}

// S4 — Run Status
// Backend pipeline stages → frontend RunStatus
const RUNNING_STAGES = new Set([
  "created",
  "normalizing",
  "blocking",
  "scoring",
  "clustering",
  "adjudicating",
  "survivorship",
]);

function normalizeRunStatus(raw: unknown): import("../types").RunStatus {
  const s = String(raw ?? "draft");
  if (s === "completed") return "completed";
  if (s === "cancelled") return "cancelled";
  if (s === "error" || s === "failed") return "failed";
  if (s === "partially_complete") return "partially_complete";
  if (s === "ready") return "ready";
  if (RUNNING_STAGES.has(s)) return "running";
  return "draft";
}

export async function getRunStatus(runId: string): Promise<MdmRun> {
  const raw = await req<Record<string, unknown>>(`/runs/${runId}`);
  return {
    id: (raw.id ?? raw.run_id ?? runId) as string,
    name: (raw.name ?? `Run ${runId.slice(0, 8)}`) as string,
    entity_type: (raw.entity_type ?? null) as
      | import("../types").EntityType
      | null,
    status: normalizeRunStatus(raw.status),
    trigger_mode: (raw.trigger_mode ??
      "manual") as import("../types").TriggerMode,
    source_count: (raw.source_count ?? 1) as number,
    created_at: (raw.created_at ?? "") as string,
    updated_at: (raw.updated_at ?? raw.created_at ?? "") as string,
    stats: raw.stats as import("../types").MdmRunStats | undefined,
    config_id: raw.config_id as string | undefined,
    paused_at: (raw.paused_at ?? null) as string | null | undefined,
    error_message: raw.error_message as string | undefined,
    _raw_status: raw.status as string | undefined,
    _raw_stats: raw.stats as Record<string, unknown> | undefined,
  } as MdmRun & {
    error_message?: string;
    _raw_status?: string;
    _raw_stats?: Record<string, unknown>;
    paused_at?: string | null;
  };
}

export async function getPairs(
  runId: string,
  filter?: PairFilter,
): Promise<PagedResult<MdmPairDecision>> {
  const params = new URLSearchParams({ status: filter?.decision ?? "pending" });
  if (filter?.cursor) params.set("cursor", filter.cursor);
  return req<PagedResult<MdmPairDecision>>(`/runs/${runId}/pairs?${params}`);
}

export async function listRuns(
  projectId: string,
  cursor?: string,
): Promise<PagedResult<MdmRun>> {
  const params = new URLSearchParams({ project_id: projectId });
  if (cursor) params.set("cursor", cursor);
  const raw = await req<{
    items: Record<string, unknown>[];
    cursor?: string;
    has_more?: boolean;
  }>(`/runs?${params}`);
  return {
    items: (raw.items ?? []).map((r) => ({
      id: (r.id ?? r.run_id ?? "") as string,
      name: (r.name ?? `Run ${String(r.run_id ?? "").slice(0, 8)}`) as string,
      entity_type: (r.entity_type ?? null) as
        | import("../types").EntityType
        | null,
      status: normalizeRunStatus(r.status),
      trigger_mode: (r.trigger_mode ??
        "manual") as import("../types").TriggerMode,
      source_count: (r.source_count ?? 1) as number,
      created_at: (r.created_at ?? "") as string,
      updated_at: (r.updated_at ?? r.created_at ?? "") as string,
    })),
    total: raw.items?.length ?? 0,
    cursor: raw.cursor,
    has_more: raw.has_more ?? false,
  };
}

export async function getConnectors(projectId?: string): Promise<
  {
    id: string;
    name: string;
    table_or_query?: string;
    last_sync_date: string;
    record_count: number;
  }[]
> {
  const params = projectId
    ? `?project_id=${encodeURIComponent(projectId)}`
    : "";
  return req<
    { id: string; name: string; last_sync_date: string; record_count: number }[]
  >(`/connectors${params}`);
}

export async function deleteConnector(connectorId: string): Promise<void> {
  await req<void>(`/connectors/${connectorId}`, { method: "DELETE" });
}

export async function connectDatabase(
  params: Record<string, unknown>,
): Promise<{
  id: string;
  name: string;
  table_or_query?: string;
  record_count: number;
  last_sync_date: string;
}> {
  return req<{
    id: string;
    name: string;
    table_or_query?: string;
    record_count: number;
    last_sync_date: string;
  }>("/connect-database", { method: "POST", body: JSON.stringify(params) });
}

export async function quickMatchEntities(
  projectId: string,
  runId: string,
  entityType: EntityType,
  entities: Record<string, string>[],
): Promise<{
  matches: Array<{
    entity_index: number;
    cluster_id: string;
    confidence: number;
    band: ConfidenceBand;
    top_attributes: Record<string, string>;
  }>;
}> {
  return req(`/runs/quick-match`, {
    method: "POST",
    body: JSON.stringify({
      project_id: projectId,
      run_id: runId,
      entity_type: entityType,
      entities,
    }),
  });
}

// ---------------------------------------------------------------------------
// Steward pair review (Epic 7)
// ---------------------------------------------------------------------------

export interface PairQueueItem {
  pair_id: string;
  posterior_score: number;
  zone: string;
  zone_override_reason?: string;
  llm_escalated: boolean;
  llm_rationale_preview?: string;
  rereview_tag?: string;
  created_at?: string;
}

export interface PairQueueResult {
  pairs: PairQueueItem[];
  next_cursor?: string;
  has_more: boolean;
}

export interface FieldScore {
  field: string;
  left_value: string;
  right_value: string;
  score: number;
  method: string;
  weight: number;
}

export interface PairDetail {
  pair: {
    id: string;
    run_id: string;
    posterior_score: number;
    zone: string;
    zone_override_reason?: string;
    llm_rationale?: string;
    field_breakdown?: FieldScore[];
    decided_at?: string;
    decision?: string;
  };
  left_record: Record<string, string>;
  right_record: Record<string, string>;
  field_breakdown: FieldScore[];
  llm_rationale?: string;
  rag_citations: { chunk_id: string; snippet: string }[];
  composite_score: number;
  thresholds: {
    auto_match_threshold: number | null;
    no_match_threshold: number | null;
  };
}

export async function getPairQueue(
  runId: string,
  cursor?: string,
  limit = 50,
): Promise<PairQueueResult> {
  const params = new URLSearchParams({
    status: "pending",
    limit: String(limit),
  });
  if (cursor) params.set("cursor", cursor);
  const raw = await req<Record<string, unknown>>(
    `/runs/${runId}/pairs?${params}`,
  );
  // Backend returns {pairs:[]} or {items:[]} depending on service version
  const pairs = (raw.pairs ?? raw.items ?? []) as PairQueueItem[];
  return {
    pairs,
    next_cursor: (raw.next_cursor ?? raw.cursor) as string | undefined,
    has_more: (raw.has_more ?? false) as boolean,
  };
}

export async function getPairDetail(pairId: string): Promise<PairDetail> {
  const raw = await req<Record<string, unknown>>(`/pairs/${pairId}`);

  // field_breakdown comes as {fieldName: {sim, comparator, field_score, ...}}
  // normalize to FieldScore[]
  const fbRaw = (raw.field_breakdown ?? {}) as Record<
    string,
    Record<string, unknown>
  >;
  const leftCj = ((raw.left_record as Record<string, unknown>)
    ?.canonical_json ?? {}) as Record<string, unknown>;
  const rightCj = ((raw.right_record as Record<string, unknown>)
    ?.canonical_json ?? {}) as Record<string, unknown>;
  const field_breakdown: FieldScore[] = Object.entries(fbRaw).map(
    ([field, v]) => ({
      field,
      left_value: String(leftCj[field] ?? ""),
      right_value: String(rightCj[field] ?? ""),
      score: (v.field_score ?? v.sim ?? 0) as number,
      method: (v.comparator ?? v.method ?? "") as string,
      weight: (v.weight ??
        ((v.m_prob as number) ?? 0.5) -
          ((v.u_prob as number) ?? 0.1)) as number,
    }),
  );

  return {
    pair: raw.pair as PairDetail["pair"],
    left_record: leftCj as Record<string, string>,
    right_record: rightCj as Record<string, string>,
    field_breakdown,
    llm_rationale: raw.llm_rationale as string | undefined,
    rag_citations: (raw.rag_citations ?? []) as {
      chunk_id: string;
      snippet: string;
    }[],
    composite_score: (raw.composite_score ??
      (raw.pair as Record<string, unknown>)?.posterior_score ??
      0) as number,
    thresholds: (raw.thresholds ?? {
      auto_match_threshold: null,
      no_match_threshold: null,
    }) as PairDetail["thresholds"],
  };
}

export async function submitPairDecision(
  pairId: string,
  decision: "match" | "no_match" | "escalate",
  stewardNotes?: string,
): Promise<{ decided: boolean; queue_drained: boolean }> {
  return req<{ decided: boolean; queue_drained: boolean }>(
    `/pairs/${pairId}/decision`,
    {
      method: "POST",
      body: JSON.stringify({ decision, steward_notes: stewardNotes ?? null }),
    },
  );
}

export async function uploadExcelSource(
  file: File,
  projectId?: string,
): Promise<{
  id: string;
  name: string;
  record_count: number;
  last_sync_date: string;
}> {
  const form = new FormData();
  form.append("file", file);
  // Attaches the upload to the project the wizard is working in; without it
  // the connector is private to the uploader.
  if (projectId) form.append("project_id", projectId);
  const res = await fetch(`${BASE}/sources/excel`, {
    method: "POST",
    body: form,
    credentials: "include",
    headers: {
      "X-CSRF-Token":
        document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(body.detail || `Upload failed: ${res.status}`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Phase 4 additions — new API client functions
// ---------------------------------------------------------------------------

/** Create a pre-loaded sample run for onboarding (Story 10.4). */
export async function createSampleRun(projectId: string): Promise<{
  run_id: string;
  config_id: string;
  records: number;
  is_sample: boolean;
}> {
  return req("/sample-runs", {
    method: "POST",
    body: JSON.stringify({ project_id: projectId }),
  });
}

/** Estimate blocking recall without running the full pipeline (Phase 1.8). */
export async function estimateBlocking(
  runId: string,
  strategies: Array<{ strategy: string; fields: string[]; threshold?: number }>,
): Promise<{
  candidate_pair_count: number;
  record_count: number;
  coverage_pct: number;
  sampled: boolean;
}> {
  return req(`/runs/${runId}/blocking/estimate`, {
    method: "POST",
    body: JSON.stringify({ strategies }),
  });
}

/** Export a match config as a portable JSON bundle (Story 5.5). */
export async function exportMatchConfig(
  configId: string,
): Promise<Record<string, unknown>> {
  return req(`/match-configs/${configId}/export`);
}

/** Validate an imported config bundle before applying (Story 5.5). */
export async function validateImportedConfig(
  payload: Record<string, unknown>,
): Promise<{ valid: boolean; errors: string[]; warnings: string[] }> {
  return req("/match-configs/validate", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Return all canonical entity type definitions (Phase 1.4). */
export async function getEntityTypes(): Promise<EntityTypeDef[]> {
  const res = await req<{ entity_types: EntityTypeDef[] }>("/entity-types");
  return res.entity_types ?? [];
}

/** Save steward field-mapping overrides to the run (Phase 1.6). */
export async function updateFieldMappings(
  runId: string,
  overrides: Array<{ source_attribute: string; canonical_field: string }>,
  excludedFields: string[] = [],
): Promise<{ run_id: string; status: string; overrides_count: number }> {
  return req(`/runs/${runId}/field-mappings`, {
    method: "PATCH",
    body: JSON.stringify({ overrides, excluded_fields: excludedFields }),
  });
}

/** Reset a stuck pipeline run back to 'created' (Phase 1.2). */
export async function resetRun(
  runId: string,
): Promise<{ run_id: string; status: string; message: string }> {
  return req(`/runs/${runId}/reset`, { method: "POST" });
}

/** Get canonical field mapping suggestions for source fields (Phase 1.5). */
export async function suggestFieldMappings(
  entityType: string,
  sourceFields: string[],
): Promise<{ entity_type: string; mappings: FieldMappingSuggestion[] }> {
  return req("/suggest-field-mappings", {
    method: "POST",
    body: JSON.stringify({
      entity_type: entityType,
      source_fields: sourceFields,
    }),
  });
}

/** Apply entity-type-derived template as the active match config (Phase 1.7). */
export async function applyEntityTemplate(
  runId: string,
  entityType?: EntityType,
): Promise<{
  config_id: string;
  entity_type: string;
  skipped_fields: string[];
  template: Record<string, unknown>;
}> {
  return req(`/runs/${runId}/apply-entity-template`, {
    method: "POST",
    body: JSON.stringify(entityType ? { entity_type: entityType } : {}),
  });
}

/** Get per-field null rates for a connector (Phase 1.1). */
export async function profileConnector(
  connectorId: string,
): Promise<ConnectorProfile> {
  return req(`/connectors/${connectorId}/profile`, { method: "POST" });
}

/** Get connection presets from the server (Phase 1.9). */
export async function getConnectionPresets(): Promise<ConnectionPreset[]> {
  const res = await req<{ presets: ConnectionPreset[] }>("/connection-presets");
  return res.presets ?? [];
}

/** Configure Oracle push target (Phase 1.10). */
export async function configureOutputTarget(
  runId: string,
  config: OracleOutputConfigRequest,
): Promise<{ output_config_id: string; table_name: string; status: string }> {
  return req(`/runs/${runId}/output-config`, {
    method: "POST",
    body: JSON.stringify(config),
  });
}

/** Poll Oracle push status (Phase 1.10). */
export async function getOutputConfig(
  runId: string,
): Promise<OracleOutputConfigResponse> {
  return req(`/runs/${runId}/output-config`);
}

/** Trigger streaming golden-record push to Oracle (Phase 1.10). */
export async function pushGoldenRecords(
  runId: string,
): Promise<{ status: string; run_id: string; output_config_id: string }> {
  return req(`/runs/${runId}/push-golden-records`, { method: "POST" });
}

/** Fetch filtered audit decisions with aggregates (Story 11.6). */
export async function getAuditDecisions(
  filters: AuditDecisionFilters = {},
): Promise<AuditDecisionsResult> {
  const params = new URLSearchParams();
  if (filters.run_id) params.set("run_id", filters.run_id);
  if (filters.decision) params.set("decision", filters.decision);
  if (filters.decision_source)
    params.set("decision_source", filters.decision_source);
  if (filters.reviewer_id) params.set("reviewer_id", filters.reviewer_id);
  if (filters.zone) params.set("zone", filters.zone);
  if (filters.decided_from) params.set("decided_from", filters.decided_from);
  if (filters.decided_to) params.set("decided_to", filters.decided_to);
  if (filters.has_override !== undefined)
    params.set("has_override", String(filters.has_override));
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  if (filters.offset !== undefined)
    params.set("offset", String(filters.offset));
  const qs = params.toString();
  return req(`/audit/decisions${qs ? `?${qs}` : ""}`);
}

/** Verify SHA-256 hash chain integrity for a run (Story 11.5). */
export async function verifyAuditChain(
  runId: string,
): Promise<{ verified: boolean; broken_at: string | null; total: number }> {
  return req(`/runs/${runId}/audit/verify`);
}

/** Build the CSV export URL for audit decisions (Story 11.6 AC-3). */
export function auditDecisionsCsvUrl(
  filters: AuditDecisionFilters = {},
): string {
  const params = new URLSearchParams();
  if (filters.run_id) params.set("run_id", filters.run_id);
  if (filters.decision) params.set("decision", filters.decision);
  if (filters.decision_source)
    params.set("decision_source", filters.decision_source);
  if (filters.reviewer_id) params.set("reviewer_id", filters.reviewer_id);
  if (filters.zone) params.set("zone", filters.zone);
  if (filters.decided_from) params.set("decided_from", filters.decided_from);
  if (filters.decided_to) params.set("decided_to", filters.decided_to);
  if (filters.has_override !== undefined)
    params.set("has_override", String(filters.has_override));
  const qs = params.toString();
  return `${BASE}/audit/decisions/export.csv${qs ? `?${qs}` : ""}`;
}
